import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { config, FeatureFlags } from '@/config'
import { logger } from '@/utils/logger'
import { Settings, Bug, Flag, Eye, EyeOff } from 'lucide-react'

export const DebugPanel: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [activeTab, setActiveTab] = useState('config')
  
  const featureFlags = FeatureFlags.getAll()
  const logStats = logger.getStats()
  const logs = logger.exportLogs().slice(-20) // Last 20 logs
  
  if (!config.debugMode) return null
  
  const toggleFeature = (feature: string) => {
    FeatureFlags.toggle(feature)
    // Force re-render by changing state
    setActiveTab(activeTab)
  }
  
  return (
    <>
      {/* Toggle Button */}
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={() => setIsVisible(!isVisible)}
          variant="outline"
          size="sm"
          className="bg-background shadow-lg border-2"
        >
          <Bug className="h-4 w-4 mr-1" />
          Debug
          {isVisible ? <EyeOff className="h-4 w-4 ml-1" /> : <Eye className="h-4 w-4 ml-1" />}
        </Button>
      </div>
      
      {/* Debug Panel */}
      {isVisible && (
        <div className="fixed bottom-16 right-4 w-96 max-h-96 z-50 bg-background border rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 bg-muted border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              <span className="font-medium text-sm">Debug Panel</span>
              <Badge variant="outline" className="text-xs">
                {config.environment}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsVisible(false)}
            >
              ×
            </Button>
          </div>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
            <TabsList className="grid w-full grid-cols-3 h-9">
              <TabsTrigger value="config" className="text-xs">Config</TabsTrigger>
              <TabsTrigger value="features" className="text-xs">Features</TabsTrigger>
              <TabsTrigger value="logs" className="text-xs">Logs</TabsTrigger>
            </TabsList>
            
            <div className="h-80 overflow-y-auto">
              <TabsContent value="config" className="p-3 space-y-3 mt-0">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Environment</h4>
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Mode:</span>
                      <Badge variant={config.environment === 'production' ? 'destructive' : 'secondary'}>
                        {config.environment}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Mock:</span>
                      <span>{config.mockMode ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Debug:</span>
                      <span>{config.debugMode ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Log Level:</span>
                      <span>{logStats.level}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">API</h4>
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">URL:</span>
                      <span className="truncate ml-2">{config.apiUrl}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Timeout:</span>
                      <span>{config.apiTimeout}ms</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Limits</h4>
                  <div className="text-xs space-y-1">
                    {Object.entries(config.limits).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-muted-foreground">{key}:</span>
                        <span>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="features" className="p-3 space-y-2 mt-0">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium flex items-center gap-1">
                    <Flag className="h-3 w-3" />
                    Feature Flags
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => FeatureFlags.reset()}
                    className="text-xs h-6"
                  >
                    Reset
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {Object.entries(featureFlags).map(([feature, enabled]) => (
                    <div key={feature} className="flex items-center justify-between py-1">
                      <span className="text-xs text-foreground">{feature}</span>
                      <Switch
                        checked={enabled}
                        onCheckedChange={() => toggleFeature(feature)}
                        className="scale-75"
                      />
                    </div>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="logs" className="p-3 space-y-2 mt-0">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium">Recent Logs</h4>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => logger.clearLogs()}
                      className="text-xs h-6"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-1 text-xs">
                  {logs.length === 0 ? (
                    <p className="text-muted-foreground">No logs available</p>
                  ) : (
                    logs.reverse().map((log, index) => (
                      <div key={index} className="pb-1 border-b border-border last:border-b-0">
                        <div className="flex items-center gap-1 mb-1">
                          <Badge 
                            variant={
                              log.level === 'error' ? 'destructive' :
                              log.level === 'warn' ? 'secondary' : 'outline'
                            }
                            className="text-xs px-1 py-0"
                          >
                            {log.level}
                          </Badge>
                          <span className="text-muted-foreground">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-foreground break-words">{log.message}</p>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      )}
    </>
  )
}